import React, { useState, useEffect } from 'react';
import {
  Box,
  HStack,
  Text,
  Icon,
  Badge,
  Tooltip,
  SimpleGrid,
  useColorModeValue,
  useToast,
} from '@chakra-ui/react';
import { FiFileText, FiExternalLink } from 'react-icons/fi';
import { useHistory } from 'react-router-dom';
import { supabase, isSupabaseConfigured } from '../../../utils/supabase';
import StageBadge from '../../../components/CRM/StageBadge';

/**
 * Identity strip for the SELECTED deal — sits above the work panel so the
 * essentials are always in front of you: primary borrower, loan #, property
 * address, loan amount.
 *
 * Matters most on party deals: when you're on a realtor's page working a deal
 * they're attached to, the borrower's name is right here — and clicking it
 * jumps to that borrower's workspace with the same deal selected.
 */

// NOTE: don't use fontSize="2xs" — it's not a token in this Chakra theme, so
// the browser drops it and text inherits the full 16px body size.
const LABEL_SIZE = '10px';
const VALUE_SIZE = 'xs';

const formatFullAmount = (amount) => {
  const n = parseFloat(amount);
  if (!n || n <= 0) return null;
  return n.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
};

function Field({ label, children, labelColor }) {
  return (
    <Box minW={0}>
      <Text
        fontSize={LABEL_SIZE}
        fontWeight="bold"
        textTransform="uppercase"
        letterSpacing="0.04em"
        color={labelColor}
        mb={0.5}
      >
        {label}
      </Text>
      {children}
    </Box>
  );
}

export default function DealIdentityCard({ deal, contact }) {
  const history = useHistory();
  const toast = useToast();

  const cardBG = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const mutedColor = useColorModeValue('gray.500', 'gray.400');
  const linkColor = useColorModeValue('purple.600', 'purple.300');

  const isPartyDeal = Boolean(deal?.contactId && contact?.id && deal.contactId !== contact.id);

  // On a party deal the borrower is someone else — fetch their name.
  // (deal.dealName is usually the borrower too, but names can be customized.)
  const [borrowerContact, setBorrowerContact] = useState(null);
  useEffect(() => {
    let cancelled = false;
    if (!isPartyDeal || !deal?.contactId || !isSupabaseConfigured()) {
      setBorrowerContact(null);
      return undefined;
    }
    supabase
      .from('crm_contacts')
      .select('id, first_name, last_name')
      .eq('id', deal.contactId)
      .maybeSingle()
      .then(({ data }) => {
        if (!cancelled) setBorrowerContact(data || null);
      });
    return () => {
      cancelled = true;
    };
  }, [deal?.contactId, isPartyDeal]);

  if (!deal) return null;

  const borrowerName = isPartyDeal
    ? (borrowerContact
        ? `${borrowerContact.first_name || ''} ${borrowerContact.last_name || ''}`.trim()
        : deal.dealName || '—')
    : `${contact.firstName || ''} ${contact.lastName || ''}`.trim() || deal.dealName || '—';

  // Street on line 1; city/state/zip on line 2 — long addresses never truncate
  const street = (deal.propertyAddress?.street || '').trim();
  const cityStateZip = [
    [deal.propertyAddress?.city, deal.propertyAddress?.state].filter(Boolean).join(', '),
    deal.propertyAddress?.zip,
  ]
    .filter(Boolean)
    .join(' ')
    .trim();
  const fullAddress = [street, cityStateZip].filter(Boolean).join(', ');

  const amount = formatFullAmount(deal.amount);
  const loanNumber = deal.loanNumber ? String(deal.loanNumber) : null;

  const copy = (text, label) => {
    navigator.clipboard.writeText(text);
    toast({ title: `${label} copied`, status: 'success', duration: 1500 });
  };

  return (
    <Box
      bg={cardBG}
      borderWidth="1px"
      borderColor={borderColor}
      borderLeftWidth="3px"
      borderLeftColor="blue.400"
      borderRadius="xl"
      shadow="sm"
      px={{ base: 4, md: 5 }}
      py={3}
    >
      <HStack justify="space-between" mb={2} flexWrap="wrap" gap={1}>
        <HStack spacing={2} flexWrap="wrap">
          <Icon as={FiFileText} color="blue.400" boxSize={3.5} />
          <Text
            fontSize={LABEL_SIZE}
            fontWeight="bold"
            textTransform="uppercase"
            letterSpacing="0.04em"
            color={mutedColor}
          >
            Selected Deal
          </Text>
          <StageBadge stage={deal.stage} />
          {isPartyDeal && (
            <Badge colorScheme="cyan" fontSize={LABEL_SIZE}>
              {contact.firstName || 'This person'} is a party on this deal
            </Badge>
          )}
        </HStack>
        {deal.ariveId && <Badge colorScheme="teal" fontSize={LABEL_SIZE}>Arive</Badge>}
      </HStack>

      <SimpleGrid columns={{ base: 2, lg: 4 }} spacingX={4} spacingY={2}>
        <Field label="Primary Borrower" labelColor={mutedColor}>
          {isPartyDeal ? (
            <Tooltip label={`Open ${borrowerName}'s workspace with this deal`}>
              <HStack
                spacing={1}
                cursor="pointer"
                color={linkColor}
                onClick={() => history.push(`/crm/workspace/${deal.contactId}?deal=${deal.id}`)}
                align="baseline"
                minW={0}
              >
                <Text fontSize={VALUE_SIZE} fontWeight="semibold" wordBreak="break-word">
                  {borrowerName}
                </Text>
                <Icon as={FiExternalLink} boxSize={3} flexShrink={0} />
              </HStack>
            </Tooltip>
          ) : (
            <Text fontSize={VALUE_SIZE} fontWeight="semibold" wordBreak="break-word">
              {borrowerName}
            </Text>
          )}
        </Field>

        <Field label="Loan #" labelColor={mutedColor}>
          {loanNumber ? (
            <Tooltip label="Click to copy">
              <Text
                fontSize={VALUE_SIZE}
                fontWeight="semibold"
                cursor="pointer"
                onClick={() => copy(loanNumber, 'Loan #')}
                _hover={{ color: 'blue.400' }}
                wordBreak="break-all"
              >
                {loanNumber}
              </Text>
            </Tooltip>
          ) : (
            <Text fontSize={VALUE_SIZE} color={mutedColor}>—</Text>
          )}
        </Field>

        <Field label="Property Address" labelColor={mutedColor}>
          {street || cityStateZip ? (
            <Tooltip label="Click to copy">
              <Box
                cursor="pointer"
                onClick={() => copy(fullAddress, 'Address')}
                _hover={{ color: 'blue.400' }}
              >
                {street && (
                  <Text fontSize={VALUE_SIZE} fontWeight="semibold" wordBreak="break-word">
                    {street}
                  </Text>
                )}
                {cityStateZip && (
                  <Text fontSize={VALUE_SIZE} fontWeight={street ? 'normal' : 'semibold'} wordBreak="break-word">
                    {cityStateZip}
                  </Text>
                )}
              </Box>
            </Tooltip>
          ) : (
            <Text fontSize={VALUE_SIZE} color={mutedColor}>—</Text>
          )}
        </Field>

        <Field label="Loan Amount" labelColor={mutedColor}>
          {amount ? (
            <Text fontSize={VALUE_SIZE} fontWeight="bold" color="green.500">
              {amount}
            </Text>
          ) : (
            <Text fontSize={VALUE_SIZE} color={mutedColor}>—</Text>
          )}
        </Field>
      </SimpleGrid>
    </Box>
  );
}
